/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  Typography
} from "@mui/material";
import Select from "react-select";
import { UploadFile } from "@mui/icons-material";
import { DateTimePicker, LocalizationProvider, DatePicker } from "@mui/lab";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import { LoadingButton } from "@mui/lab";
import { CKEditor } from "ckeditor4-react";
import moment from "moment";
import { notification } from "src/utils/messages";
import { Routes } from "src/constant/api_url";
import { PollSelect } from "src/utils/common";
import styled from "styled-components";
import ScheduleEntryList from "./ScheduleEntryList";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1
});

const imageTypes = ["png", "jpg", "jpeg", "gif", "webp"];
const videoTypes = ["mp3", "wav", "ogg"];

function NotificationForm({
  formData,
  setFormData,
  page,
  onSubmit,
  onCancel,
  isFormSubmit,
  setFile,
  fileimage,
  setfileimage,
  loginUser,
  scheduleReadOnly,
  onScheduleEntryRemove,
  sentLocked,
  siblingHasImmediate
}) {
  const [option, setOption] = useState(null);

  const isEditMode = page === 2;
  const isAddMode = page === 1;
  // Type + targeting can only be locked when this notification was already sent.
  const configLocked = !!sentLocked;

  const getOptionsValueList = async (type = 0) => {
    let optionData = [];
    if (type != 0) {
      const resp = await PollSelect(type, loginUser?.u_type, loginUser?.city_id);
      resp?.map((e) => optionData.push({ value: e.value, label: e.name }));
      setOption(optionData);
    }
  };

  useEffect(() => {
    if (formData.n_for_type != 0) {
      getOptionsValueList(formData.n_for_type);
    }
  }, [formData.n_for_type]);

  const getYoutubeVideoId = (url) => {
    if (!url) return null;
    const regExp = /^.*(?:youtu\.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=|shorts\/)([^#&?]{11}).*/;
    const match = url.match(regExp);
    return match ? `https://www.youtube.com/embed/${match[1]}` : null;
  };

  const getAcceptType = (type) => {
    switch (type) {
      case "0":
      case 0:
        return "image/*,.jpg,.jpeg,.png,.gif";
      case "1":
      case 1:
        return "audio/*,.mp3,.wav,.ogg";
      default:
        return "";
    }
  };

  const handleTypeChange = (e) => {
    if (isAddMode) {
      setFormData({
        ...formData,
        n_type: e.target.value,
        n_title: "",
        n_description: "",
        n_link: "",
        n_button_title: "",
        n_image: "",
        n_audio: "",
        n_video: "",
        n_question: "",
        n_answer: "",
        schedule_entries: [
          {
            s_date: moment().format("DD-MM-YYYY"),
            e_date: "",
            notification_send_type: "0",
            n_date_time: "",
            show_on_home: 0,
            _uid: `entry_${Date.now()}`
          }
        ]
      });
      setFile({});
      setfileimage();
    } else {
      setFormData({ ...formData, n_type: e.target.value });
    }
  };

  const handleFileChange = (event) => {
    const uploadedFile = event.currentTarget.files[0];
    if (!uploadedFile) return;

    let maxSize = 2 * 1024 * 1024;
    if (formData.n_type == 1) maxSize = 10 * 1024 * 1024;

    const allowedImageTypes = ["image/jpeg", "image/png", "image/jpg", "image/gif"];
    const allowedAudioTypes = ["audio/mpeg", "audio/wav", "audio/ogg"];

    if (formData.n_type == 0 && !allowedImageTypes.includes(uploadedFile.type)) {
      notification({ type: "error", message: "Please upload a valid image file.", position: "top" })();
      return;
    }
    if (formData.n_type == 1 && !allowedAudioTypes.includes(uploadedFile.type)) {
      notification({ type: "error", message: "Please upload a valid audio file.", position: "top" })();
      return;
    }
    if (uploadedFile.size > maxSize) {
      notification({
        type: "error",
        message: `File size exceeds ${maxSize / (1024 * 1024)}MB. Please upload a smaller file.`,
        position: "top"
      })();
      return;
    }

    setFile({ file: uploadedFile });
    setfileimage(URL.createObjectURL(uploadedFile));
  };

  const handleFileInputTypeChange = (e) => {
    if (isAddMode) {
      setFormData({
        ...formData,
        n_type: e.target.value
      });
      setFile({});
      setfileimage();
    } else {
      setFormData({ ...formData, n_type: e.target.value });
    }
  };

  return (
    <Card style={{ padding: "20px" }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom style={{ textAlign: "center" }}>
            {isAddMode ? "Add New Notification" : "Edit Notification"}
          </Typography>
          <hr style={{ marginBottom: "0", marginTop: "0" }} />
        </Grid>

        {/* Select Type */}
        <Grid item xs={12}>
          <FormLabel>Select Type</FormLabel>
          <Stack spacing={3}>
            <RadioGroup
              row
              value={formData?.n_type}
              onChange={handleTypeChange}
              name="n_type"
              disabled={configLocked}
            >
              <FormControlLabel value={0} control={<Radio />} label="Photo" disabled={configLocked} />
              <FormControlLabel value={1} control={<Radio />} label="Audio" disabled={configLocked} />
              <FormControlLabel value={2} control={<Radio />} label="Video" disabled={configLocked} />
              <FormControlLabel
                value={3}
                control={<Radio />}
                label="Question and Answer"
                disabled={configLocked}
              />
            </RadioGroup>
          </Stack>
        </Grid>

        {/* Notification Send To */}
        <Grid item xs={12}>
          <FormLabel>Notification Send To</FormLabel>
          <Stack spacing={3}>
            <RadioGroup
              row
              value={formData?.n_send_to != null ? String(formData.n_send_to) : ""}
              onChange={(e) => setFormData({ ...formData, n_send_to: e.target.value })}
              name="n_send_to"
              disabled={configLocked}
            >
              <FormControlLabel value="0" control={<Radio />} label="All" disabled={configLocked} />
              <FormControlLabel value="1" control={<Radio />} label="LT" disabled={configLocked} />
              <FormControlLabel value="2" control={<Radio />} label="Members" disabled={configLocked} />
              <FormControlLabel value="3" control={<Radio />} label="Director" disabled={configLocked} />
            </RadioGroup>
          </Stack>
        </Grid>

        {/* Notification For */}
        <Grid item xs={12}>
          <FormLabel>Notification For</FormLabel>
          <Stack spacing={3}>
            <RadioGroup
              row
              value={formData?.n_for_type}
              onChange={(e) => {
                setFormData({ ...formData, n_for_type: e.target.value, n_for_id: "0" });
              }}
              name="n_for_type"
              disabled={configLocked}
            >
              {loginUser?.u_type == 0 && (
                <>
                  <FormControlLabel value="0" control={<Radio />} label="LVB" disabled={configLocked} />
                  <FormControlLabel value="1" control={<Radio />} label="State" disabled={configLocked} />
                </>
              )}
              <FormControlLabel value="2" control={<Radio />} label="City" disabled={configLocked} />
              <FormControlLabel value="3" control={<Radio />} label="Chapter" disabled={configLocked} />
            </RadioGroup>
          </Stack>
        </Grid>

        {/* Multi-select State/City/Chapter */}
        {formData.n_for_type != 0 && (
          <Grid item xs={12}>
            <Stack spacing={10} style={{ marginBottom: "15px" }}>
              <label style={{ marginBottom: "10px" }}>
                Select
                {formData?.n_for_type == 1
                  ? " State"
                  : formData?.n_for_type == 2
                    ? " City"
                    : formData?.n_for_type == 3
                      ? " Chapter"
                      : " LVB"}
              </label>
              <Select
                isMulti
                required={true}
                isDisabled={configLocked}
                isOptionDisabled={() => configLocked}
                value={
                  option?.filter((opt) => {
                    const raw = (formData?.n_for_id || "0").replace(/^\[|]$/g, "");
                    const ids = raw.split(",").filter(Boolean);
                    return ids.includes(String(opt.value));
                  }) || []
                }
                onChange={(selected) => {
                  const val = selected ? selected.map((s) => s.value).join(",") : "0";
                  setFormData({ ...formData, n_for_id: val });
                }}
                menuPortalTarget={document.body}
                options={option}
                placeholder="Select..."
              />
            </Stack>
          </Grid>
        )}

        {/* Notification To */}
        {(loginUser?.u_type == 0 ||
          (loginUser?.u_type == 0 && loginUser?.is_subadmin == 1)) && (
          <Grid item xs={12}>
            <FormLabel>Notification To</FormLabel>
            <Stack spacing={3}>
              <RadioGroup
                row
                value={formData?.n_to != null ? String(formData.n_to) : ""}
                onChange={(e) => setFormData({ ...formData, n_to: e.target.value })}
                name="n_to"
                disabled={configLocked}
              >
                <FormControlLabel value="0" control={<Radio />} label="All" disabled={configLocked} />
                <FormControlLabel value="1" control={<Radio />} label="Not OBT (New Member)" disabled={configLocked} />
                <FormControlLabel value="2" control={<Radio />} label="Not Green Tick (OBT Done)" disabled={configLocked} />
                <FormControlLabel value="3" control={<Radio />} label="Not Blue Tick (V2V Done)" disabled={configLocked} />
                <FormControlLabel value="6" control={<Radio />} label="Blue Tick Done" disabled={configLocked} />
                <FormControlLabel value="4" control={<Radio />} label="Not Fillup V2V Sheet" disabled={configLocked} />
                <FormControlLabel value="5" control={<Radio />} label="Location Not Set" disabled={configLocked} />
                <FormControlLabel value="9" control={<Radio />} label="Not OBT or Not Green Tick" disabled={configLocked} />
                <FormControlLabel value="10" control={<Radio />} label="Not Green Tick or Not Blue Tick" disabled={configLocked} />
                <FormControlLabel value="11" control={<Radio />} label="Not OBT or Not Green Tick or Not Blue Tick" disabled={configLocked} />
              </RadioGroup>
            </Stack>
          </Grid>
        )}

        {/* Title and Description (Photo/Audio/Video) */}
        {formData?.n_type != 3 && (
          <>
            <Grid item xs={12}>
              <Stack spacing={3}>
                <TextField
                  required
                  type="text"
                  label="Title"
                  fullWidth
                  value={formData?.n_title}
                  onChange={(e) => setFormData({ ...formData, n_title: e.target.value })}
                />
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <FormLabel>Description</FormLabel>
              <CKEditor
                initData={formData?.n_description}
                config={{
                  allowedContent: true,
                  extraPlugins: "colorbutton,font",
                  removeButtons: "",
                  colorButton_colors:
                    "000000,993300,333300,003300,003366,000080,333399,333333," +
                    "800000,FF6600,808000,008000,008080,0000FF,666699,808080," +
                    "FF0000,FF9900,99CC00,339966,33CCCC,3366FF,800080,969696," +
                    "FF00FF,FFCC00,FFFF00,00FF00,00FFFF,00CCFF,993366,C0C0C0," +
                    "FF99CC,FFCC99,FFFF99,CCFFCC,CCFFFF,99CCFF,CC99FF,FFFFFF," +
                    "123456,FFA500,4CAF50,9C27B0,2196F3,034766"
                }}
                onChange={(event) => {
                  const data = event.editor.getData();
                  setFormData((prev) => ({ ...prev, n_description: data }));
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <Stack spacing={3}>
                <TextField
                  type="text"
                  label="URL"
                  fullWidth
                  value={formData?.n_link}
                  onChange={(e) => setFormData({ ...formData, n_link: e.target.value })}
                />
              </Stack>
            </Grid>
            <Grid item xs={6}>
              <Stack spacing={3}>
                <TextField
                  type="text"
                  label="Button Title"
                  fullWidth
                  value={formData?.n_button_title}
                  onChange={(e) => setFormData({ ...formData, n_button_title: e.target.value })}
                />
              </Stack>
            </Grid>
          </>
        )}

        {/* File Upload (Photo/Audio) */}
        {(formData?.n_type == 0 || formData?.n_type == 1) && (
          <Grid item xs={12}>
            <Button
              component="label"
              variant="contained"
              color="primary"
              tabIndex={-1}
              sx={{ textTransform: "none", p: "10px" }}
              startIcon={<UploadFile />}
            >
              Click to Upload file
              <VisuallyHiddenInput
                type="file"
                required
                multiple={false}
                accept={getAcceptType(formData?.n_type)}
                onChange={handleFileChange}
              />
            </Button>

            {/* Preview */}
            {fileimage !== undefined && fileimage ? (
              <div style={{ marginTop: "10px" }}>
                {formData?.n_type == 0 ? (
                  <>
                    <h4>Image Preview</h4>
                    <img
                      src={fileimage}
                      alt="Uploaded file preview"
                      style={{
                        width: "450px",
                        objectFit: "contain",
                        height: "250px",
                        boxShadow: "0 0 5px #ccc",
                        borderRadius: "5px"
                      }}
                    />
                  </>
                ) : (
                  <>
                    <h4>Audio Preview</h4>
                    <audio controls src={fileimage} />
                  </>
                )}
              </div>
            ) : isEditMode && formData?.n_image ? (
              <div style={{ marginTop: "10px" }}>
                {formData?.n_type == 0 &&
                imageTypes?.includes(formData?.n_image?.split(".").pop()?.toLowerCase()) ? (
                  <>
                    <h4>Image Preview</h4>
                    <img
                      src={`${Routes.API_HOSTNAME}images/notification/${formData?.n_image}`}
                      alt="Uploaded file preview"
                      style={{
                        width: "450px",
                        objectFit: "contain",
                        height: "250px",
                        boxShadow: "0 0 5px #ccc",
                        borderRadius: "5px"
                      }}
                    />
                  </>
                ) : formData?.n_type == 1 &&
                  videoTypes?.includes(formData?.n_image?.split(".").pop()?.toLowerCase()) ? (
                  <>
                    <h4>Audio Preview</h4>
                    <audio
                      controls
                      src={`${Routes.API_HOSTNAME}images/notification/${formData?.n_image}`}
                    />
                  </>
                ) : null}
              </div>
            ) : null}
          </Grid>
        )}

        {/* Video URL */}
        {formData?.n_type == 2 && (
          <>
            <Grid item xs={12}>
              <Stack spacing={3}>
                <TextField
                  required
                  type="text"
                  label="Video URL"
                  fullWidth
                  value={formData?.n_video}
                  onChange={(e) => setFormData({ ...formData, n_video: e.target.value })}
                />
              </Stack>
            </Grid>
            {formData?.n_video != "" && getYoutubeVideoId(formData?.n_video) != null && (
              <Grid item xs={12}>
                <div
                  className="rounded"
                  style={{ position: "relative", height: "250px", overflow: "hidden", maxWidth: "100%" }}
                >
                  <iframe
                    style={{ position: "absolute", top: 0, left: 0, width: "450px", height: "250px" }}
                    src={getYoutubeVideoId(formData?.n_video)}
                    title="YouTube video player"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              </Grid>
            )}
          </>
        )}

        {/* Question and Answer */}
        {formData?.n_type == 3 && (
          <>
            <Grid item xs={12}>
              <Stack spacing={3}>
                <TextField
                  required
                  type="text"
                  label="Question"
                  fullWidth
                  value={formData?.n_question}
                  onChange={(e) => setFormData({ ...formData, n_question: e.target.value })}
                />
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <FormLabel>Answer</FormLabel>
              <CKEditor
                initData={formData?.n_answer}
                config={{
                  allowedContent: true,
                  extraPlugins: "colorbutton,font",
                  removeButtons: "",
                  colorButton_colors:
                    "000000,993300,333300,003300,003366,000080,333399,333333," +
                    "800000,FF6600,808000,008000,008080,0000FF,666699,808080," +
                    "FF0000,FF9900,99CC00,339966,33CCCC,3366FF,800080,969696," +
                    "FF00FF,FFCC00,FFFF00,00FF00,00FFFF,00CCFF,993366,C0C0C0," +
                    "FF99CC,FFCC99,FFFF99,CCFFCC,CCFFFF,99CCFF,CC99FF,FFFFFF," +
                    "123456,FFA500,4CAF50,9C27B0,2196F3,034766"
                }}
                onChange={(event) => {
                  const data = event.editor.getData();
                  setFormData((prev) => ({ ...prev, n_answer: data }));
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <Stack spacing={3}>
                <TextField
                  type="text"
                  label="URL"
                  fullWidth
                  value={formData?.n_link}
                  onChange={(e) => setFormData({ ...formData, n_link: e.target.value })}
                />
              </Stack>
            </Grid>
            <Grid item xs={6}>
              <Stack spacing={3}>
                <TextField
                  type="text"
                  label="Button Title"
                  fullWidth
                  value={formData?.n_button_title}
                  onChange={(e) => setFormData({ ...formData, n_button_title: e.target.value })}
                />
              </Stack>
            </Grid>
          </>
        )}

        {/* Schedule Entries (Multi-Date) */}
        <Grid item xs={12}>
          <ScheduleEntryList
            entries={formData?.schedule_entries || []}
            setEntries={(entries) => setFormData({ ...formData, schedule_entries: entries })}
            isEditMode={isEditMode}
            readOnly={scheduleReadOnly}
            onEntryRemove={onScheduleEntryRemove}
            siblingHasImmediate={siblingHasImmediate}
          />
        </Grid>

        {/* Submit and Cancel */}
        <Grid item xs={12} spacing={2}>
          <LoadingButton
            type="button"
            variant="contained"
            color="primary"
            sx={{ marginRight: "5px" }}
            loading={isFormSubmit}
            onClick={() => onSubmit()}
          >
            Submit
          </LoadingButton>
          <Button variant="outlined" color="error" onClick={onCancel}>
            Cancel
          </Button>
        </Grid>
      </Grid>
    </Card>
  );
}

export default NotificationForm;
